import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { ArrowDown, ArrowUp, Plus, RotateCcw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface ListEditorProps {
    id: string
    items: string[]
    onChange: (items: string[]) => void
    defaults: string[]
    placeholder: string
    addLabel: string
}

const ListEditor: FC<ListEditorProps> = ({ id, items, onChange, defaults, placeholder, addLabel }): ReactNode => {
    const [draft, setDraft] = useState('')
    const [error, setError] = useState<string | null>(null)

    const add = () => {
        const value = draft.trim()
        if (!value) return

        if (items.some(item => item.toLowerCase() === value.toLowerCase())) {
            setError(`"${value}" is already in the list.`)
            return
        }

        onChange([...items, value])
        setDraft('')
        setError(null)
    }

    const move = (index: number, direction: -1 | 1) => {
        const target = index + direction
        if (target < 0 || target >= items.length) return

        const next = [...items]
        const [item] = next.splice(index, 1)
        next.splice(target, 0, item)
        onChange(next)
    }

    const changed = JSON.stringify(items) !== JSON.stringify(defaults)

    return (
        <div className='flex flex-col gap-3'>
            <ul className='flex flex-col divide-y divide-border rounded-xl border border-border'>
                {items.length === 0 && (
                    <li className='px-3 py-4 text-center text-sm text-muted-foreground'>
                        The list is empty.
                    </li>
                )}

                {items.map((item, index) => (
                    <li key={item} className='flex items-center gap-2 px-3 py-1.5'>
                        <span className='flex-1 truncate text-sm text-foreground'>
                            {item}
                        </span>

                        <Button type='button' variant='ghost' size='icon-xs' aria-label={`Move ${item} up`} disabled={index === 0} onClick={() => move(index, -1)}>
                            <ArrowUp />
                        </Button>

                        <Button type='button' variant='ghost' size='icon-xs' aria-label={`Move ${item} down`} disabled={index === items.length - 1} onClick={() => move(index, 1)}>
                            <ArrowDown />
                        </Button>

                        <Button
                            type='button'
                            variant='ghost'
                            size='icon-xs'
                            aria-label={`Remove ${item}`}
                            className='text-muted-foreground hover:text-destructive'
                            onClick={() => onChange(items.filter(entry => entry !== item))}
                        >
                            <X />
                        </Button>
                    </li>
                ))}
            </ul>

            <div className='flex gap-2'>
                <label htmlFor={id} className='sr-only'>
                    {addLabel}
                </label>

                <Input
                    id={id}
                    value={draft}
                    onChange={event => {
                        setDraft(event.target.value)
                        setError(null)
                    }}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            add()
                        }
                    }}
                    placeholder={placeholder}
                />

                <Button type='button' variant='outline' onClick={add} disabled={!draft.trim()}>
                    <Plus />
                    Add
                </Button>
            </div>

            {error && (
                <p role='alert' className='text-xs font-medium text-destructive'>
                    {error}
                </p>
            )}

            {changed && (
                <Button type='button' variant='link' size='sm' className='h-auto self-start px-0' onClick={() => onChange([...defaults])}>
                    <RotateCcw />
                    Reset to the default list
                </Button>
            )}
        </div>
    )
}

export default ListEditor
